import { useEffect, useState } from 'react';
import { friendlyError } from '../../shared/api/errors.js';
import { useNavigate } from 'react-router-dom';
import {
  COUNTRY_DEFAULTS,
  DEFAULT_COUNTRY,
  type BusinessType,
  type Country,
} from '@acs/shared';
import { Banner, Button, Field, Select, Skeleton } from '@acs/ui';
import { useMyBusinesses } from '../../app/BusinessesContext.js';
import { BusinessFields, Step, WizardFrame } from '../onboarding/OnboardingWizardPage.js';
import { needsOnboarding } from '../onboarding/onboardingSteps.js';
import { useCreateBusiness } from './hooks.js';
import { useI18n } from '../../i18n/index.js';
import { newBusinessLanguage } from './newBusinessLanguage.js';

/**
 * Step 1 of setup, for a business that doesn't exist yet.
 *
 * Creating a business *is* the first onboarding step rather than a separate
 * form in front of it — the owner fills in one form, once. Nothing is written
 * until they continue, so Cancel here genuinely creates nothing; once the
 * business exists there's something to come back to, and the wizard offers
 * "Save & exit" instead.
 *
 * Business facts only. The assistant's language and greeting used to be asked
 * for here, which put "what should the robot say?" in front of an owner who had
 * not yet been told they were getting a phone number — they now have their own
 * step, after the number (`onboardingSteps.ts`). Timezone is seeded from the
 * country, language as `newBusinessLanguage` says; both are confirmed later.
 */
export function CreateBusinessPage() {
  const { t, locale } = useI18n();
  const { active: businesses, loading } = useMyBusinesses();
  const navigate = useNavigate();
  const create = useCreateBusiness();
  /** Set once the business exists server-side; cleared by navigating away. */
  const [createdId, setCreatedId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  // Asked first here (doc 23): nothing is committed yet. The wizard's first
  // step can still correct it; once setup is finished it is fixed.
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [copyFrom, setCopyFrom] = useState('');

  // Step 2 of the same wizard, the moment the new business is actually
  // resolvable. No seam for the owner to notice: the button simply stays busy
  // for the extra beat the listener needs. Above the early returns below — a
  // hook after a conditional return runs on some renders and not others.
  useEffect(() => {
    if (createdId && businesses.some((b) => b.id === createdId)) {
      navigate(`/b/${createdId}/setup?step=services`, { replace: true });
    }
  }, [createdId, businesses, navigate]);

  if (loading) return <Skeleton height={420} radius="var(--tc-radius-lg)" />;

  const valid = name.trim().length > 0 && businessType !== null;
  const firstBusiness = businesses.length === 0;
  // What "start from an existing business" may copy: a business that finished
  // setup. Never the one being created — the listener delivers it while this
  // page waits to move on, and the field used to pop in offering to copy the
  // new business into itself — and never a draft still in setup, which is not
  // yet a business to the owner and has nothing worth copying. No sources, no
  // field: an owner's first business never sees it.
  const copySources = businesses.filter((b) => b.id !== createdId && !needsOnboarding(b));

  const submit = () =>
    create.mutate(
      {
        name: name.trim(),
        ...(businessType ? { businessType } : {}),
        country,
        // Timezone comes from the country; the language from the portal
        // language picked at sign-up for a first business, else the country
        // (`newBusinessLanguage`). The owner confirms (or changes) the
        // language on the Assistant step; `greeting` is deliberately omitted so
        // that step can tell "never written" from "deliberately blank" — and so
        // a copy-from source's greeting isn't overwritten by a generated one.
        language: newBusinessLanguage(country, locale, firstBusiness),
        timezone: COUNTRY_DEFAULTS[country].timezone,
        ...(copyFrom ? { copyFromBusinessId: copyFrom } : {}),
      },
      // Do NOT navigate here.
      //
      // `ActiveBusinessLayout` resolves `/b/:id` against the owner's business
      // list, which is fed by a Firestore listener. The callable returns before
      // that listener has delivered the new document, so navigating on success
      // lands on a route whose business "does not exist" — and the layout's
      // foreign-id guard sends the owner to the businesses list instead of into
      // step 2. That is the bug this replaces: the first step appeared to dump
      // people out of the wizard entirely.
      //
      // Unlike the other steps this cannot be fixed by seeding the cache: the
      // callable returns an id and nothing else, and inventing the rest of a
      // business document to satisfy a router is how a screen ends up rendering
      // fields the server never agreed to. So we hold, and the effect below
      // moves as soon as the document is really there.
      { onSuccess: (id) => setCreatedId(id) },
    );

  return (
    <WizardFrame active={0} firstUnfinished={0}>
      <Step
        title={t(firstBusiness ? 'createBusiness.titleFirst' : 'createBusiness.titleAnother')}
        lede={t('createBusiness.lede')}
        backSlot={
          <Button variant="ghost" disabled={create.isPending} onClick={() => navigate('/businesses')}>
            Cancel
          </Button>
        }
        next={{
          label: t('createBusiness.cta'),
          loading: create.isPending || createdId !== null,
          disabled: !valid,
          hint: t('createBusiness.nameRequired'),
          onClick: submit,
        }}
      >
        <BusinessFields
          name={name}
          onName={setName}
          businessType={businessType}
          onBusinessType={setBusinessType}
          country={country}
          onCountry={setCountry}
        />

        {copySources.length > 0 && (
          <Field
            label={t('createBusiness.copyFrom')}
            hint={t('createBusiness.copyFromHint')}
          >
            <Select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">{t('createBusiness.startEmpty')}</option>
              {copySources.map((b) => (
                <option key={b.id} value={b.id}>
                  Copy from {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {create.isError && (
          <Banner tone="danger">
            {friendlyError(t, create.error, t('createBusiness.failed'))}
          </Banner>
        )}
      </Step>
    </WizardFrame>
  );
}
